import React, { useState, useEffect } from "react"
import "../../Settings.css"
import "./PTOPlanMaintenance.css"
import PTOPlanModal from "./PTOPlanModal";
import { Table, Alert } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Axios from 'axios';
import AxiosConfig from '../../../../utilities/AxiosConfig';
import Loader from '../../../../components/common/Loader';

// interface Props {

// }

function PTOPlanMaintenance(/*props: Props*/) {
    const [loading, setLoading] = useState(false as boolean);
    const [showModal, setShowModal] = useState(false as boolean);
    const [tableRows, setTableRows] = useState([] as planData[]);
    const [currentRowData, setCurrentRowData] = useState({} as planData | null);
    const [lastSorted, setLastSorted] = useState<string>();
    const [showErrorMessages, setShowErrorMessages] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    interface singleColumn {
        columnKey: string;
        columnName: string;
    };
    interface PTOPlanDetailsSingleRow {
        StartingMonth: number;
        EndingMonth: number;
        AccrualAmount: number;
        MaxAccrualCeilingHours: number;
        HourlyAccrualAmount: number
        PTOPlanId?: number | null;
        PTOPlanDetailId?: number | null;
        CreatedBy?: number | null;
        ModifiedBy?: number | null;
        DeletedBy?: number | null;
    }
    interface planData {
        [key: string]: any;    // index signature
        PTOPlanName: string;
        Description: string;
        PlanPayType: string;
        MaxDailyPTOHours: number;
        AccrualWaitingPeriodDays: number;
        UsingWaitingPeriodDays: number;
        CarryOverMaxHours: number;
        PTOPlanId: number | null;
        DeletedBy?: number | null;
        PayOutPercent: number | null
        PTOHourlyCalculationMethodId: number | null | string
        PTOPlanDetails: PTOPlanDetailsSingleRow[];
    };

    // columns that will be shown on the `PTO plan maintenance` table in the order that they will appear
    const columns: singleColumn[] = [
        { columnKey: 'PTOPlanName', columnName: 'PTO Plan Name' },  // varchar(250)
        { columnKey: 'Description', columnName: 'Description' },    // varchar(500)
        { columnKey: 'PlanPayType', columnName: 'Plan Type' },      // varchar(40)
        { columnKey: 'MaxDailyPTOHours', columnName: 'Max Daily PTO Hours' },   // int
        { columnKey: 'AccrualWaitingPeriodDays', columnName: 'Accrual Waiting Period Days' },   // int
        { columnKey: 'UsingWaitingPeriodDays', columnName: 'Using Waiting Period Days' },       // int
        { columnKey: 'CarryOverMaxHours', columnName: 'Carry Over Max Hours' }  // int
    ];

    const getPTOPlans = () => {
        setLoading(true);

        Axios.get(`${process.env.REACT_APP_API_URL}/api/pto/plans`, AxiosConfig.configValue())
            .then(response => {
                const tempTableRows: planData[] = [];
                if (!response.data || response.data.length === 0) {
                    setErrorMessage('There was an error getting plan table data.');
                    setShowErrorMessages(true);
                    return; // page should show an empty table and an error should pop up
                }

                //r-18-lint-id
                //eslint-disable-next-line
                response.data.map((planData: { plan: planData, details: Object[] }) => {
                    const tempPlanDetailRow: PTOPlanDetailsSingleRow[] = [];
                    //r-18-lint-id
                    //eslint-disable-next-line
                    planData.details.map((detail: any) => {
                        const tempSingleDetailRow: PTOPlanDetailsSingleRow = {
                            StartingMonth: detail.StartingMonth,
                            EndingMonth: detail.EndingMonth,
                            AccrualAmount: detail.AccrualAmount,
                            MaxAccrualCeilingHours: detail.MaxAccrualCeilingHours,
                            HourlyAccrualAmount: detail.HourlyAccrualAmount,
                            PTOPlanDetailId: detail.PTOPlanDetailId,
                            PTOPlanId: detail.PTOPlanId
                        };
                        tempPlanDetailRow.push(tempSingleDetailRow);
                    });

                    const tempRow: planData = {
                        PTOPlanName: planData.plan.PTOPlanName,
                        Description: planData.plan.Description,
                        PlanPayType: planData.plan.PlanPayType,
                        MaxDailyPTOHours: planData.plan.MaxDailyPTOHours,
                        AccrualWaitingPeriodDays: planData.plan.AccrualWaitingPeriodDays,
                        UsingWaitingPeriodDays: planData.plan.UsingWaitingPeriodDays,
                        CarryOverMaxHours: planData.plan.CarryOverMaxHours,
                        PTOPlanId: planData.plan.PTOPlanId,
                        PayOutPercent: planData.plan.PayOutPercent,
                        PTOHourlyCalculationMethodId: planData.plan.PTOHourlyCalculationMethodId,
                        PTOPlanDetails: tempPlanDetailRow
                    };
                    tempTableRows.push(tempRow);
                });
                setTableRows(tempTableRows);
            }).catch(error => {
                setErrorMessage('There was an error getting plan table data.');
                setShowErrorMessages(true);
            }).finally(() => { setLoading(false); });
    };

    // used with the `OverlayTrigger` tag, returns a tooltip with custom text
    const renderDescriptionTooltip = (text: string) => (
        <Tooltip id={`tooltip-${text}`}>
            {text}
        </Tooltip>
    );

    const sortTable = (column: string) => {
        if (column === lastSorted) {    // this means that it has already been sorted in asc, now needs to be desc
            tableRows.reverse();
        } else {    // this means it is the first time it has been clicked, and needs to be asc
            tableRows.sort((a, b) => {  // 0: leaves `a` in same spot. 1: moves `a` down the list. -1: moves `a` up the list 
                if (a[column] === b[column]) return 0;
                if (a[column] === null) return 1;
                if (a[column] === undefined) return 1;
                if (a[column] === '') return 1;
                if (b[column] === null) return -1;
                if (a[column] >= b[column]) return 1;
                return -1;
            });
        }
        setTableRows([...tableRows]);
        setLastSorted(column);
    };

    // on initial render, gets pto plan data
    useEffect(() => {
        getPTOPlans();
        //r-18-lint-id
        //eslint-disable-next-line
    }, []);

    return (
        <div className="PTOPlanMaintenance Settings main-content col-sm PTOPlanMaintenanceTableScroll">
            {loading && (
                <Loader title='Loading PTO Plans Table' />
            )}
            {!loading && (
                <>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                {columns.map((col) => (
                                    <th>
                                        <div className='PTOPlanMaintenanceTableHeader'>
                                            {col.columnName}
                                            <span className="material-icons-outlined PTOPlanMaintenanceButtonHover" onClick={() => sortTable(col.columnKey)}>unfold_more</span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tfoot className='PTOPlanMaintenanceTableFooter'>
                            <tr>
                                <th>
                                    {tableRows.length !== 0 && (
                                        <OverlayTrigger overlay={renderDescriptionTooltip('Add Plan')}>
                                            <span className="material-icons-outlined PTOPlanMaintenanceButtonHover" onClick={() => { setCurrentRowData(null); setShowModal(true); }}>add_circle</span>
                                        </OverlayTrigger>
                                    )}
                                </th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                            </tr>
                        </tfoot>
                        <tbody>
                            {tableRows.map((row) => (
                                <tr onClick={() => { setCurrentRowData(row); setShowModal(true); }}>
                                    <>
                                        {columns.map((col) => (
                                            <td>
                                                {row[col.columnKey as keyof planData]}
                                            </td>
                                        ))}
                                    </>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {showModal && (
                        <PTOPlanModal
                            showModal={showModal}
                            setShowModal={setShowModal}
                            descriptionTooltip={renderDescriptionTooltip}
                            currentRowData={currentRowData}
                            getPTOPlans={getPTOPlans}
                            setErrorMessage={setErrorMessage}
                            setShowErrorMessages={setShowErrorMessages}
                        />
                    )}
                    <Alert className="PTOPlanMaintenanceAlertContainer" variant='danger' show={showErrorMessages} onClose={() => { setShowErrorMessages(false); setErrorMessage(''); }} dismissible>
                        <p>{errorMessage}</p>
                    </Alert>
                </>
            )}

        </div>
    );
}

export default PTOPlanMaintenance